import React, { useEffect, useState } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Modal,
  Table,
  Tag,
  Typography,
  message,
  Popconfirm,
} from 'antd'
import { DeleteOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons'
import {
  adminsService,
  type AdminAccountRow,
  type CreateAdminPayload,
  type UpdateAdminPayload,
} from '@services/adminsService'
import { useAppSelector } from '@store'

const { Title, Text } = Typography
const { Option } = Select

const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: '超级管理员',
  ADMIN: '管理员',
  OPERATOR: '操作员',
}

/** 超级管理员：子账号列表、授权与删除（与系统设置内区块一致，供独立页复用） */
const AdminAccountsPanel: React.FC = () => {
  const currentAdmin = useAppSelector((s) => s.auth.admin)
  const [createAdminForm] = Form.useForm()
  const [adminList, setAdminList] = useState<AdminAccountRow[]>([])
  const [adminListLoading, setAdminListLoading] = useState(false)
  const [createAdminOpen, setCreateAdminOpen] = useState(false)
  const [createAdminSubmitting, setCreateAdminSubmitting] = useState(false)

  const loadAdminAccounts = async () => {
    try {
      setAdminListLoading(true)
      const list = await adminsService.list()
      setAdminList(list || [])
    } catch (e) {
      console.error(e)
      message.error('加载管理员列表失败')
    } finally {
      setAdminListLoading(false)
    }
  }

  useEffect(() => {
    loadAdminAccounts()
  }, [])

  return (
    <>
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TeamOutlined style={{ fontSize: '18px', color: '#1890ff', marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>管理员账号</Title>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createAdminForm.resetFields()
              createAdminForm.setFieldsValue({
                role: 'OPERATOR',
                status: 'ACTIVE',
              })
              setCreateAdminOpen(true)
            }}
          >
            添加账号
          </Button>
        </div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          仅超级管理员（母账号）可在此授权、调整或删除子账号；新建子账号须绑定中国大陆手机号（可用手机号登录）；请至少保留一名可用的超级管理员。
        </Text>
        <Table<AdminAccountRow>
          rowKey="id"
          loading={adminListLoading}
          size="small"
          pagination={false}
          dataSource={adminList}
          columns={[
            { title: '用户名', dataIndex: 'username', key: 'username' },
            { title: '手机号', dataIndex: 'phone', key: 'phone', width: 120 },
            {
              title: '角色',
              key: 'role',
              width: 160,
              render: (_, record) => {
                if (record.id !== currentAdmin?.id) {
                  return (
                    <Select
                      value={record.role}
                      style={{ width: 140 }}
                      onChange={async (v) => {
                        try {
                          await adminsService.update(record.id, {
                            role: v as CreateAdminPayload['role'],
                          })
                          message.success('角色已更新')
                          loadAdminAccounts()
                        } catch (err: unknown) {
                          message.error(err instanceof Error ? err.message : '更新失败')
                        }
                      }}
                    >
                      <Option value="SUPER_ADMIN">超级管理员</Option>
                      <Option value="ADMIN">管理员</Option>
                      <Option value="OPERATOR">操作员</Option>
                    </Select>
                  )
                }
                return <Tag>{ROLE_LABEL[record.role] || record.role}</Tag>
              },
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              render: (st: string, record) => {
                const canToggle = record.id !== currentAdmin?.id
                return (
                  <Select
                    value={st}
                    disabled={!canToggle}
                    style={{ width: 110 }}
                    onChange={async (v) => {
                      try {
                        await adminsService.update(record.id, {
                          status: v as UpdateAdminPayload['status'],
                        })
                        message.success('状态已更新')
                        loadAdminAccounts()
                      } catch (err: unknown) {
                        message.error(err instanceof Error ? err.message : '更新失败')
                      }
                    }}
                  >
                    <Option value="ACTIVE">正常</Option>
                    <Option value="INACTIVE">已禁用</Option>
                  </Select>
                )
              },
            },
            {
              title: '操作',
              key: 'act',
              width: 100,
              render: (_, record) => {
                if (record.id === currentAdmin?.id) {
                  return <Text type="secondary">当前账号</Text>
                }
                return (
                  <Popconfirm
                    title="确定删除该管理员？"
                    onConfirm={async () => {
                      try {
                        await adminsService.remove(record.id)
                        message.success('已删除')
                        loadAdminAccounts()
                      } catch (err: unknown) {
                        message.error(err instanceof Error ? err.message : '删除失败')
                      }
                    }}
                  >
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>
                )
              },
            },
          ]}
        />
      </Card>

      <Modal
        title="添加管理员"
        open={createAdminOpen}
        confirmLoading={createAdminSubmitting}
        onCancel={() => {
          setCreateAdminOpen(false)
          createAdminForm.resetFields()
        }}
        destroyOnClose
        onOk={() => createAdminForm.submit()}
        okText="创建"
      >
        <Form
          form={createAdminForm}
          layout="vertical"
          onFinish={async (v) => {
            setCreateAdminSubmitting(true)
            try {
              const phone = String(v.phone || '').replace(/\s/g, '')
              const payload: CreateAdminPayload = {
                phone,
                password: v.password,
                role: v.role || 'OPERATOR',
                email: v.email,
                status: v.status || 'ACTIVE',
              }
              const un = String(v.username || '').trim()
              if (un) payload.username = un
              await adminsService.create(payload)
              message.success('管理员已创建')
              setCreateAdminOpen(false)
              createAdminForm.resetFields()
              loadAdminAccounts()
            } catch (e: unknown) {
              const msg =
                e && typeof e === 'object' && 'message' in e
                  ? String((e as { message: string }).message)
                  : '创建失败'
              message.error(msg)
            } finally {
              setCreateAdminSubmitting(false)
            }
          }}
        >
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              {
                pattern: /^1[3-9]\d{9}$/,
                message: '请输入11位中国大陆手机号',
              },
            ]}
          >
            <Input placeholder="子账号登录标识，必填" maxLength={11} autoComplete="tel" />
          </Form.Item>
          <Form.Item
            name="username"
            label="登录用户名"
            tooltip="不填则默认与手机号相同，仍可用手机号登录"
            rules={[
              {
                validator: (_, value) => {
                  const s = String(value || '').trim()
                  if (!s) return Promise.resolve()
                  if (s.length >= 3) return Promise.resolve()
                  return Promise.reject(new Error('至少3个字符，或留空使用手机号作为登录名'))
                },
              },
            ]}
          >
            <Input placeholder="选填，默认使用手机号" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '至少6位' },
            ]}
          >
            <Input.Password placeholder="至少6位" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select placeholder="选择角色">
              <Option value="OPERATOR">操作员</Option>
              <Option value="ADMIN">管理员</Option>
              <Option value="SUPER_ADMIN">超级管理员</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="ACTIVE">
            <Select>
              <Option value="ACTIVE">正常</Option>
              <Option value="INACTIVE">已禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input placeholder="选填" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default AdminAccountsPanel
